<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import FileIcon from 'next/icon/FileIcon.vue';
import TicketsAPI from 'dashboard/api/tickets';
import SelectConversationAttachmentModal from './SelectConversationAttachmentModal.vue';
import { getFileInfo } from '@chatwoot/utils';
import { isTicketAttachmentMimeAllowed } from 'dashboard/helper/ticketAttachmentMime';

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

const props = defineProps({
  ticket: {
    type: Object,
    required: true,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update']);

const { t } = useI18n();
const showSelectModal = ref(false);
const isUnlinking = ref(null);
const isUploading = ref(false);
const isDragging = ref(false);
const uploadInputRef = ref(null);

const attachments = computed(() => props.ticket?.attachments || []);
const hasConversation = computed(() => !!props.ticket?.conversation);

const getFileDetails = dataUrl => {
  return getFileInfo(dataUrl || '');
};

const getDisplayFileName = dataUrl => {
  const { base, type } = getFileDetails(dataUrl);
  const truncatedName = (str, maxLength, hasExt) =>
    str.length > maxLength
      ? `${str.substring(0, maxLength).trimEnd()}${hasExt ? '..' : '...'}`
      : str;

  return type
    ? `${truncatedName(base, 12, true)}.${type}`
    : truncatedName(base, 14, false);
};

const getFileType = attachment => {
  if (attachment.file_type === 'image') return 'image';
  if (attachment.file_type === 'video') return 'video';
  if (attachment.file_type === 'audio') return 'audio';
  return getFileDetails(attachment.data_url).type || 'file';
};

const isImageType = attachment => attachment.file_type === 'image';

const onAttachmentLinked = async attachment => {
  try {
    await TicketsAPI.linkAttachment(props.ticket.display_id, attachment.id);
    showSelectModal.value = false;
    emit('update');
    useAlert(t('TICKETS.ATTACHMENTS.LINK_SUCCESS'));
  } catch (error) {
    if (error.response?.status === 422) {
      useAlert(t('TICKETS.ATTACHMENTS.ALREADY_LINKED'));
    } else {
      useAlert(t('TICKETS.ATTACHMENTS.LINK_ERROR'));
    }
  }
};

const unlinkAttachment = async attachmentId => {
  isUnlinking.value = attachmentId;
  try {
    await TicketsAPI.unlinkAttachment(props.ticket.display_id, attachmentId);
    emit('update');
    useAlert(t('TICKETS.ATTACHMENTS.UNLINK_SUCCESS'));
  } catch (error) {
    useAlert(t('TICKETS.ATTACHMENTS.UNLINK_ERROR'));
  } finally {
    isUnlinking.value = null;
  }
};

const uploadFile = async file => {
  if (!isTicketAttachmentMimeAllowed(file.type)) {
    useAlert(t('TICKETS.ATTACHMENTS.INVALID_FILE_TYPE'));
    return;
  }
  if (file.size > MAX_FILE_SIZE) {
    useAlert(t('TICKETS.ATTACHMENTS.FILE_TOO_LARGE'));
    return;
  }
  isUploading.value = true;
  try {
    await TicketsAPI.uploadAttachment(props.ticket.display_id, file);
    emit('update');
    useAlert(t('TICKETS.ATTACHMENTS.UPLOAD_SUCCESS'));
  } catch (error) {
    useAlert(t('TICKETS.ATTACHMENTS.UPLOAD_ERROR'));
  } finally {
    isUploading.value = false;
  }
};

const onUploadInput = async e => {
  const files = Array.from(e.target.files || []);
  for (const file of files) {
    await uploadFile(file);
  }
  e.target.value = '';
};

const onDragOver = e => {
  e.preventDefault();
  isDragging.value = true;
};

const onDragLeave = e => {
  if (e.currentTarget.contains(e.relatedTarget)) return;
  isDragging.value = false;
};

const onDrop = async e => {
  e.preventDefault();
  isDragging.value = false;
  const files = Array.from(e.dataTransfer.files || []);
  for (const file of files) {
    await uploadFile(file);
  }
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex items-center justify-between">
      <h4 class="text-xs font-medium text-n-slate-11">
        {{ $t('TICKETS.ATTACHMENTS.TITLE') }}
      </h4>
      <div v-if="!readonly" class="flex items-center gap-1">
        <Button
          v-if="hasConversation"
          variant="ghost"
          size="xs"
          icon="i-lucide-link"
          @click="showSelectModal = true"
        >
          {{ $t('TICKETS.ATTACHMENTS.LINK_FROM_CONVERSATION') }}
        </Button>
        <Button
          variant="ghost"
          size="xs"
          icon="i-lucide-upload"
          :is-loading="isUploading"
          :disabled="isUploading"
          @click="uploadInputRef?.click()"
        >
          {{ $t('TICKETS.ATTACHMENTS.UPLOAD_FILE') }}
        </Button>
        <input
          ref="uploadInputRef"
          type="file"
          multiple
          class="sr-only"
          @change="onUploadInput"
        />
      </div>
    </div>

    <!-- Drag & drop zone (only when not readonly) -->
    <div
      v-if="!readonly"
      class="flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-3 py-3 transition-colors cursor-pointer"
      :class="
        isDragging
          ? 'border-n-brand bg-n-brand/5'
          : 'border-n-weak hover:border-n-brand/50 hover:bg-n-alpha-1'
      "
      @dragover="onDragOver"
      @dragleave="onDragLeave"
      @drop="onDrop"
      @click="uploadInputRef?.click()"
    >
      <span
        class="i-lucide-upload-cloud w-5 h-5"
        :class="isDragging ? 'text-n-brand' : 'text-n-slate-10'"
      />
      <span class="text-xs text-n-slate-10 text-center">
        {{
          isDragging
            ? $t('TICKETS.ATTACHMENTS.DROP_HERE')
            : $t('TICKETS.ATTACHMENTS.DRAG_AND_DROP_HINT')
        }}
      </span>
    </div>

    <!-- Attachments Grid -->
    <div v-if="attachments.length" class="flex flex-wrap gap-2">
      <div
        v-for="attachment in attachments"
        :key="attachment.id"
        class="relative group"
      >
        <!-- Image attachment -->
        <div
          v-if="isImageType(attachment)"
          class="size-16 overflow-hidden rounded-lg border border-n-weak"
        >
          <a
            :href="attachment.data_url"
            target="_blank"
            rel="noreferrer noopener"
          >
            <img
              class="object-cover w-full h-full"
              :src="attachment.thumb_url || attachment.data_url"
              :alt="getDisplayFileName(attachment.data_url)"
            />
          </a>
        </div>

        <!-- File attachment -->
        <div
          v-else
          class="h-9 bg-n-alpha-white gap-2 overflow-hidden items-center flex px-2 rounded-lg border border-n-container"
        >
          <FileIcon class="flex-shrink-0" :file-type="getFileType(attachment)" />
          <span
            class="flex-1 min-w-0 text-sm max-w-28 text-n-slate-12"
            :title="getFileDetails(attachment.data_url).name"
          >
            {{ getDisplayFileName(attachment.data_url) }}
          </span>
          <a
            class="flex-shrink-0 size-6 grid place-content-center cursor-pointer text-n-slate-11 hover:text-n-slate-12 transition-colors"
            :href="attachment.data_url"
            rel="noreferrer noopener nofollow"
            target="_blank"
          >
            <Icon icon="i-lucide-download" class="size-4" />
          </a>
        </div>

        <!-- Unlink button -->
        <button
          v-if="!readonly"
          class="absolute -top-1.5 -right-1.5 p-0.5 rounded-full bg-n-ruby-9 text-white opacity-0 group-hover:opacity-100 transition-opacity"
          :disabled="isUnlinking === attachment.id"
          @click="unlinkAttachment(attachment.id)"
        >
          <Icon icon="i-lucide-x" class="size-3" />
        </button>
      </div>
    </div>

    <p v-else class="text-xs text-n-slate-10 italic">
      {{ $t('TICKETS.ATTACHMENTS.NO_ATTACHMENTS') }}
    </p>

    <!-- Select from Conversation Modal -->
    <SelectConversationAttachmentModal
      v-if="showSelectModal"
      :show="showSelectModal"
      :ticket="ticket"
      :linked-attachment-ids="attachments.map(a => a.id)"
      @close="showSelectModal = false"
      @select="onAttachmentLinked"
    />
  </div>
</template>
